import { Color, CustomCalendarModel, SimpleActivity } from "@ac-expo/api-types";
import { EventScheduleType, extractTimetableDates, formatRGBColor, getStartAndEndOfWeek, isTicketTypeWithinSalePeriod } from "@ac-expo/utils";
import type React from "react";
import { format, isAfter, isBefore, max, min, parse } from "date-fns";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/fr";
import { groupBy, max as lodashMax, min as lodashMin } from "lodash";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("fr");

export interface PlanningEvent {
  id: string;
  activityId?: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  isFree?: boolean;
  createdBy: string;
  isRegistered?: boolean;
  isRegistrationRequired: boolean;
  isAvailableOnDisabledTicketing?: boolean;
  eventTicketing?: SimpleActivity["event"]["ticketing"];
  blockColor?: Color;
  hasAvailableTicketTypes?: boolean;
  areaName?: string;
  weekStart?: Date;
}

export interface PlanningEventGroup extends Omit<
  PlanningEvent,
  "activityId" | "isFree" | "createdBy"
> {
  allEvents: PlanningEvent[];
}

export const isPlanningGroup = (
  event: PlanningEvent | PlanningEventGroup,
): event is PlanningEventGroup => !!("allEvents" in event && event.allEvents);

const doEventOverlap = (
  event1: Pick<PlanningEvent, "start" | "end">,
  event2: Pick<PlanningEvent, "start" | "end">,
) => event1.start < event2.end && event2.start < event1.end;

export const groupEventsByTime = (
  events: PlanningEvent[],
): (PlanningEvent | PlanningEventGroup)[] => {
  const groups: PlanningEventGroup[] = [];

  events.forEach((event) => {
    let addedToGroup = false;

    for (const group of groups) {
      if (doEventOverlap(event, group) && group.allDay === event.allDay) {
        group.allEvents.push(event);
        group.start = min([group.start, event.start]).toISOString();
        group.end = max([group.end, event.end]).toISOString();
        addedToGroup = true;
        break;
      }
    }

    if (!addedToGroup) {
      groups.push({
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        allDay: event.allDay,
        allEvents: [event],
        isRegistrationRequired: event.isRegistrationRequired,
      });
    }
  });

  return groups.map((g) => (g.allEvents.length > 1 ? g : g.allEvents[0]));
};

export const getSlots = (
  activity: SimpleActivity,
  isPublicExponentDisplay?: boolean,
): PlanningEvent[] =>
  activity.schedules.map((s) => {
    const { start, end } = extractTimetableDates(s.timetable);

    const title =
      isPublicExponentDisplay && activity.event?.name
        ? `${activity.name} (${activity.event.name})`
        : activity.name;

    return {
      id: activity.id + "-" + s.id,
      activityId: activity.id,
      title: title,
      start: format(start, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
      end: format(end, "yyyy-MM-dd'T'HH:mm:ss.SSS"),
      allDay: s.timetable.isFullDay,
      isFree: activity.isFree,
      createdBy: activity.structureId ? "Exposant" : "Organisateur",
      isRegistered: activity.ticketTypes.some((tt) => tt.tickets.length > 0),
      hasAvailableTicketTypes: activity.ticketTypes.some(isTicketTypeWithinSalePeriod),
      isRegistrationRequired: activity.registrationRequired,
      isAvailableOnDisabledTicketing:
        activity.structureSchedule ||
        (activity.organiserSchedule !== null && activity.structureId !== null && activity.isOnline),
      eventTicketing: activity.event.ticketing,
      blockColor: activity.blockColor,
      areaName: activity.activityArea?.areaName ?? "",
      weekStart: getStartAndEndOfWeek(start).startOfWeek,
    };
  });

export const getMinMaxTimes = (activities: SimpleActivity[]) => {
  const times = activities
    .flatMap((event) => [
      dayjs(event.schedules[0].timetable.startHour, "H:m"),
      dayjs(event.schedules[0].timetable.endHour, "H:m"),
    ])
    .filter((d) => d.isValid());

  if (times.length === 0) {
    return { minTime: "00:00:00", maxTime: "00:00:00" };
  }

  const minTime = dayjs(Math.min(...times.map((time) => time.valueOf())));
  const maxTime = dayjs(Math.max(...times.map((time) => time.valueOf())));

  return {
    minTime: minTime.format("HH:mm:ss"),
    maxTime: maxTime.format("HH:mm:ss"),
  };
};

export type EventSchedulesTimes = {
  timetables: Pick<EventScheduleType["timetables"][0], "openingTime" | "closingTime">[];
};

export const findMinMaxScheduleTimes = (eventSchedules: EventSchedulesTimes[] | undefined) => {
  if (!eventSchedules || eventSchedules.length === 0) {
    return null;
  }

  let minTime = parse("23:59", "HH:mm", new Date());
  let maxTime = parse("00:00", "HH:mm", new Date());

  eventSchedules.forEach((schedule) => {
    const startTime = parse(schedule.timetables[0]?.openingTime ?? "00:00", "HH:mm", new Date());
    const endTime = parse(schedule.timetables[0]?.closingTime ?? "23:59", "HH:mm", new Date());
    if (isBefore(startTime, minTime)) {
      minTime = startTime;
    }
    if (isAfter(endTime, maxTime)) {
      maxTime = endTime;
    }
  });

  return {
    minTime: format(minTime, "HH:mm:ss"),
    maxTime: format(maxTime, "HH:mm:ss"),
  };
};

export const getMinMaxEventTimes = (
  activities: SimpleActivity[],
  eventSchedules: EventSchedulesTimes[] | undefined,
) => {
  const { minTime: activitiesMinTime, maxTime: activitiesMaxTime } = getMinMaxTimes(activities);
  const minMaxScheduleTimes = findMinMaxScheduleTimes(eventSchedules);

  if (!minMaxScheduleTimes) {
    return {
      minTime: activitiesMinTime,
      maxTime: activitiesMaxTime,
    };
  }

  return {
    minTime: lodashMin([activitiesMinTime, minMaxScheduleTimes.minTime]),
    maxTime: lodashMax([activitiesMaxTime, minMaxScheduleTimes.maxTime]),
  };
};

export const getCalendarView = (customCalendar?: CustomCalendarModel): string => {
  switch (customCalendar) {
    case "verticalDayHour":
      return "timeGridSevenDay";
    case "verticalDayList":
      return "listSevenDay";
    case "verticalWeekly":
      return "listSevenDay";
    case "horizontalMonthly":
      return "dayGridMonth";
    case "verticalByPlace":
      // verticalByPlace has custom rendering, but fallback to dayGridMonth
      return "dayGridMonth";
    default:
      return "dayGridMonth";
  }
};

export type BackgroundStyleOptions = {
  backgroundPictureUrl?: string;
  backgroundColor?: Color | string;
  opacity?: number;
};

export const getBackgroundContainerStyle = (
  options: BackgroundStyleOptions,
): React.CSSProperties => ({
  backgroundImage: options.backgroundPictureUrl
    ? `url(${options.backgroundPictureUrl})`
    : undefined,
  backgroundPosition: "center",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
});

export const getBackgroundOverlayStyle = (
  options: BackgroundStyleOptions,
): React.CSSProperties => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  backgroundColor: formatRGBColor(options.backgroundColor ?? "white"),
  opacity: (100 - (options.opacity ?? 100)) / 100,
});

/**
 * Merges events on consecutive days with the same time slots.
 * Used only for display in horizontal timeline mode.
 */
export const mergeConsecutiveDaySlots = (slots: PlanningEvent[]): PlanningEvent[] => {
  if (slots.length === 0) return [];

  const buildGroupKey = (slot: PlanningEvent): string => {
    const startTime = dayjs(slot.start).format("HH:mm");
    const endTime = dayjs(slot.end).format("HH:mm");
    return `${slot.activityId ?? "no-activity"}-${startTime}-${endTime}-${slot.allDay}`;
  };

  const groups = groupBy(slots, buildGroupKey);

  return Object.values(groups).flatMap((group) => {
    if (group.length === 1) return group;

    const sortedByDate = [...group].sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

    return sortedByDate.reduce<PlanningEvent[]>((merged, slot, index) => {
      const previousSlot = sortedByDate[index - 1];
      const isConsecutiveDay =
        previousSlot && dayjs(slot.start).diff(dayjs(previousSlot.start), "day") === 1;

      if (isConsecutiveDay && merged.length > 0) {
        const lastMerged = merged[merged.length - 1];
        merged[merged.length - 1] = {
          ...lastMerged,
          id: `merged-${lastMerged.activityId}-${lastMerged.start}-${slot.start}`,
          end: slot.end,
        };
        return merged;
      }

      return [...merged, slot];
    }, []);
  });
};

export interface WeekGroup {
  weekStart: Date;
  weekEnd: Date;
  activities: PlanningEvent[];
}

export const groupActivitiesByWeek = (
  activities: PlanningEvent[],
  currentMonth?: Date
): WeekGroup[] => {
  if (activities.length === 0) return [];

  const grouped = groupBy(activities, (activity) => {
    const { startOfWeek } = getStartAndEndOfWeek(new Date(activity.start));
    return startOfWeek.toISOString();
  });

  const weeks: WeekGroup[] = Object.entries(grouped)
    .map(([weekStartISO, weekActivities]) => {
      const weekStart = new Date(weekStartISO);
      const { endOfWeek: weekEnd } = getStartAndEndOfWeek(weekStart);
      return {
        weekStart,
        weekEnd,
        activities: weekActivities.sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        ),
      };
    })
    .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime());

  if (!currentMonth) return weeks;

  const monthStart = dayjs(currentMonth).startOf("month");
  const monthEnd = dayjs(currentMonth).endOf("month");

  return weeks.filter((week) => {
    const weekStartDayjs = dayjs(week.weekStart);
    const weekEndDayjs = dayjs(week.weekEnd);
    return (
      weekStartDayjs.isSameOrBefore(monthEnd, "day") &&
      weekEndDayjs.isSameOrAfter(monthStart, "day")
    );
  });
};

export const formatWeekRange = (weekStart: Date, weekEnd: Date): string => {
  const start = dayjs(weekStart);
  const end = dayjs(weekEnd);

  if (start.month() === end.month()) {
    return `${start.format("D")} - ${end.format("D")} ${start.format("MMMM")}`;
  }
  return `${start.format("D MMM")} - ${end.format("D MMM")}`;
};

export const getDaysOfWeek = (weekStart: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => dayjs(weekStart).add(i, "day").toDate());

export const getActivitiesForDay = (
  activities: PlanningEvent[],
  day: Date
): PlanningEvent[] =>
  activities.filter((activity) => dayjs(activity.start).isSame(dayjs(day), "day"));

export const formatActivityDays = (activity: PlanningEvent, weekActivities: PlanningEvent[]): string => {
  const sameActivitySlots = weekActivities.filter(
    (a) => a.activityId === activity.activityId
  );

  if (sameActivitySlots.length <= 1) {
    return dayjs(activity.start).format("dddd");
  }

  const dayNames = sameActivitySlots
    .map((slot) => dayjs(slot.start).format("dddd"))
    .filter((v, i, a) => a.indexOf(v) === i);

  return dayNames.join(", ");
};

export const formatActivityTime = (activity: PlanningEvent): string => {
  if (activity.allDay) {
    return "Jour entier";
  }
  const start = dayjs(activity.start).format("HH:mm");
  const end = dayjs(activity.end).format("HH:mm");
  return `${start} - ${end}`;
};

export const getUniqueActivities = (activities: PlanningEvent[]): PlanningEvent[] => {
  const seen = new Set<string>();
  return activities.filter((activity) => {
    if (!activity.activityId || seen.has(activity.activityId)) return false;
    seen.add(activity.activityId);
    return true;
  });
};

export interface ActivitySpan {
  activity: PlanningEvent;
  startColIndex: number;
  span: number;
}

const findDayIndexInWeek = (slotStart: string, weekDays: Date[]): number => {
  const slotDay = dayjs(slotStart);
  return weekDays.findIndex((day) => dayjs(day).isSame(slotDay, "day"));
};

const groupConsecutiveIndices = (
  sortedIndices: number[],
  activity: PlanningEvent
): ActivitySpan[] => {
  const spans: ActivitySpan[] = [];
  let spanStart = sortedIndices[0];
  let prevIdx = sortedIndices[0];

  for (let i = 1; i <= sortedIndices.length; i++) {
    const isLastIteration = i === sortedIndices.length;
    const isConsecutive = sortedIndices[i] === prevIdx + 1;

    if (isLastIteration || !isConsecutive) {
      spans.push({
        activity,
        startColIndex: spanStart,
        span: prevIdx - spanStart + 1,
      });

      if (!isLastIteration) {
        spanStart = sortedIndices[i];
      }
    }
    prevIdx = sortedIndices[i];
  }

  return spans;
};

/**
 * Groups activity slots by activityId and calculates consecutive day spans within a week.
 * Non-consecutive days result in separate spans.
 */
export const getActivitySpansForWeek = (
  activities: PlanningEvent[],
  weekDays: Date[]
): ActivitySpan[] => {
  const activityGroups = groupBy(activities, "activityId");
  const spans: ActivitySpan[] = [];

  for (const slots of Object.values(activityGroups)) {
    if (slots.length === 0) continue;

    const dayIndices = slots
      .map((slot) => findDayIndexInWeek(slot.start, weekDays))
      .filter((idx) => idx >= 0)
      .sort((a, b) => a - b);

    if (dayIndices.length === 0) continue;

    const activitySpans = groupConsecutiveIndices(dayIndices, slots[0]);
    spans.push(...activitySpans);
  }

  return spans.sort((a, b) => a.startColIndex - b.startColIndex);
};

export const DAY_HEADERS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"] as const;

export const GRID_COLUMN_DIVIDERS = [1, 2, 3, 4, 5, 6] as const;

export const getActivityBackgroundColor = (
  isAllDay: boolean,
  fullDayColor?: Color | string,
  blockColor?: Color | string
): string => {
  if (isAllDay) {
    return formatRGBColor(fullDayColor ?? "orange");
  }
  return formatRGBColor(blockColor ?? "blue");
};
